import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./TransitionCounter.module.scss";
import gsap from "gsap";
import { useRefStore } from "../../../store/ref-store";
import { useProgress } from "@react-three/drei";

export default function TransitionCounter() {
  const counterThreeRef = useRef(null);
  const counterTwoRef = useRef(null);
  const counterOneRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const { loaded: threeLoaded, total: threeTotal } = useProgress();
  const [loadedCounts, setLoadedCounts] = useState({ images: 0, videos: 0 });
  const [assetTotals, setAssetTotals] = useState({ images: 0, videos: 0 });
  const [displayProgress, setDisplayProgress] = useState(0);

  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const hasCompletedRef = useRef(false);

  const combinedProgress = useMemo(() => {
    const totalAssets = assetTotals.images + assetTotals.videos;
    const loadedAssets = loadedCounts.images + loadedCounts.videos;
    const totalToLoad = threeTotal + totalAssets;

    if (totalToLoad === 0) {
      return 100;
    }

    const totalLoaded = threeLoaded + loadedAssets;
    return Math.min(100, (totalLoaded / totalToLoad) * 100);
  }, [assetTotals, loadedCounts, threeLoaded, threeTotal]);

  const getAllImages = useCallback(async () => {
    const images = document.querySelectorAll("img");
    const sourcesSet = new Set();

    images.forEach((element) => {
      const src = element.getAttribute("src");
      if (src) {
        sourcesSet.add(src);
      }
    });

    const sources = Array.from(sourcesSet);
    return sources;
  }, []);

  const loadAllImages = useCallback(async (sources) => {
    if (sources.length === 0) return;

    const loadPromises = sources.map(
      (src) =>
        new Promise((resolve) => {
          const image = new Image();
          image.onload = () => {
            setLoadedCounts((prev) => ({ ...prev, images: prev.images + 1 }));
            resolve(null);
          };
          image.onerror = () => {
            setLoadedCounts((prev) => ({ ...prev, images: prev.images + 1 }));
            resolve(null);
          };
          image.src = src;
        })
    );

    await Promise.all(loadPromises);
  }, []);

  const getAllVideos = useCallback(async () => {
    const videos = document.querySelectorAll("video");
    const sourcesSet = new Set();

    videos.forEach((element) => {
      const source =
        element.getAttribute("src") ||
        element.children[0]?.getAttribute("src");
      if (source) {
        sourcesSet.add(source);
      }
    });

    const sources = Array.from(sourcesSet);
    return sources;
  }, []);

  const loadAllVideos = useCallback(async (sources) => {
    if (sources.length === 0) return;

    const loadPromises = sources.map(
      (src) =>
        new Promise<void>((resolve) => {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.muted = true;
          video.playsInline = true;

          const done = () => {
            clearTimeout(perVideoTimeout);
            setLoadedCounts((prev) => ({ ...prev, videos: prev.videos + 1 }));
            resolve();
          };

          video.onloadedmetadata = done;
          video.onerror = done;

          const perVideoTimeout = setTimeout(done, 5000);

          video.src = src;
          video.load();
        })
    );

    await Promise.all(loadPromises);
  }, []);

  const loadAllAssets = useCallback(async () => {
    const imageSources = await getAllImages();
    const videoSources = await getAllVideos();

    setAssetTotals({
      images: imageSources.length,
      videos: videoSources.length,
    });

    await loadAllImages(imageSources);
    await loadAllVideos(videoSources);
  }, [getAllImages, getAllVideos, loadAllImages, loadAllVideos]);

  useEffect(() => {
    let rafId = 0;
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        loadAllAssets();
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [loadAllAssets]);

  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (!hasCompletedRef.current) {
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
          completionTimeoutRef.current = null;
        }
        setDisplayProgress(100);
        hasCompletedRef.current = true;
      }
    }, 8000);

    return () => clearTimeout(safetyTimeout);
  }, []);

  useEffect(() => {
    if (combinedProgress >= 100) {
      if (hasCompletedRef.current || completionTimeoutRef.current) {
        return;
      }

      completionTimeoutRef.current = setTimeout(() => {
        setDisplayProgress(100);
        hasCompletedRef.current = true;
        completionTimeoutRef.current = null;
      }, 500);

      return;
    }

    hasCompletedRef.current = false;

    if (completionTimeoutRef.current) {
      clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }

    setDisplayProgress(combinedProgress);
  }, [combinedProgress]);

  useEffect(() => {
    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isAnimating && displayProgress >= 0) {
      const clampedProgress = Math.min(100, Math.max(0, displayProgress));

      const ones = clampedProgress % 10;
      const tens = Math.floor((clampedProgress % 100) / 10);
      const hundreds = Math.floor(clampedProgress / 100);

      const numHeight =
        counterThreeRef.current?.querySelector("div")?.clientHeight || 0;

      if (numHeight > 0) {
        gsap.to(counterOneRef.current, {
          y: clampedProgress === 100 ? -numHeight : -hundreds * numHeight,
          duration: 0.3,
          ease: "power2.out",
        });

        gsap.to(counterTwoRef.current, {
          y: clampedProgress === 100 ? -10 * numHeight : -tens * numHeight,
          duration: 0.3,
          ease: "power2.out",
        });

        const targetIndex = clampedProgress === 100 ? 10 : ones;
        gsap.to(counterThreeRef.current, {
          y: -targetIndex * numHeight,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    }
  }, [displayProgress, isAnimating]);

  useEffect(() => {
    if (displayProgress >= 100 && isAnimating) {
      setTimeout(() => {
        gsap.to(`.${styles.digit}`, {
          top: "-150px",
          stagger: 0.025,
          duration: 1.25,
          ease: "power4.out",
          onComplete: () => {
            useRefStore.setState({ counterTemp: false });
          },
        });
      }, 500);
    }
  }, [displayProgress, isAnimating]);

  useEffect(() => {
    counterThreeRef.current.innerHTML = "";
    counterTwoRef.current.innerHTML = "";
    counterOneRef.current.innerHTML = "";

    const centenaOutline = document.createElement("div");
    centenaOutline.className = `${styles.num} ${styles.numOutline}`;
    centenaOutline.textContent = "0";
    counterOneRef.current.appendChild(centenaOutline);

    const centenaOne = document.createElement("div");
    centenaOne.className = `${styles.num} ${styles.numOneOffset}`;
    centenaOne.textContent = "1";
    counterOneRef.current.appendChild(centenaOne);

    // Decenas: 0 outline, 1-9, 0 sin outline
    const decenaOutline = document.createElement("div");
    decenaOutline.className = `${styles.num} ${styles.numOutline}`;
    decenaOutline.textContent = "0";
    counterTwoRef.current.appendChild(decenaOutline);

    for (let j = 1; j <= 9; j++) {
      const div = document.createElement("div");
      div.className = `${styles.num} ${j === 1 ? styles.numTwoOffset : ""}`;
      div.textContent = j.toString();
      counterTwoRef.current.appendChild(div);
    }

    const decenaFinal = document.createElement("div");
    decenaFinal.className = `${styles.num}`;
    decenaFinal.textContent = "0";
    counterTwoRef.current.appendChild(decenaFinal);

    // Unidades: 0 outline, 1-9, 0 sin outline
    const unidadOutline = document.createElement("div");
    unidadOutline.className = `${styles.num} ${styles.numOutline}`;
    unidadOutline.textContent = "0";
    counterThreeRef.current.appendChild(unidadOutline);

    for (let j = 1; j <= 9; j++) {
      const div = document.createElement("div");
      div.className = `${styles.num}`;
      div.textContent = j.toString();
      counterThreeRef.current.appendChild(div);
    }

    // Agregar un 0 final sin outline para mostrar cuando llegue a 100
    const unidadFinal = document.createElement("div");
    unidadFinal.className = `${styles.num}`;
    unidadFinal.textContent = "0";
    counterThreeRef.current.appendChild(unidadFinal);

    setIsAnimating(true);
  }, []);

  return (
    <div className={styles.counter}>
      {/* Centenas (0-1) - se generan dinámicamente */}
      <div
        className={`${styles.counterOne} ${styles.digit}`}
        ref={counterOneRef}
      />

      {/* Decenas (0-9, 0) - se generan dinámicamente */}
      <div
        className={`${styles.counterTwo} ${styles.digit}`}
        ref={counterTwoRef}
      />

      {/* Unidades (0-9, 0) - se generan dinámicamente */}
      <div
        className={`${styles.counterThree} ${styles.digit}`}
        ref={counterThreeRef}
      />
    </div>
  );
}
